import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useContext, useCallback, useState, useEffect, useRef } from 'react';
import { SEARCH_QUERY_KEY, SEARCH_FILTERS_KEY, SEARCH_SHOW_AI_DIALOG_KEY, getSessionItem, setSessionItem, deleteSessionItem } from '@atlassian/search-dialog';
import { useAnalytics, SearchDialogAnalyticsContext, onStickySearchCleared } from '../../common/analytics';
var noop = function noop() {};
var SESSION_RESET_TIMEOUT_MS = 300000;
var anyConfluenceFiltersChecked = function anyConfluenceFiltersChecked(filters) {
  var confluencefilters = filters.confluence;
  if (confluencefilters) {
    var _confluencefilters$sp, _confluencefilters$sp2, _confluencefilters$pe, _confluencefilters$pe2, _confluencefilters$si, _confluencefilters$si2;
    var hasSpaceFilterChecked = (_confluencefilters$sp = confluencefilters.space) !== null && _confluencefilters$sp !== void 0 && _confluencefilters$sp.length ? (_confluencefilters$sp2 = confluencefilters.space) === null || _confluencefilters$sp2 === void 0 ? void 0 : _confluencefilters$sp2.some(function (filter) {
      return filter.isChecked;
    }) : false;
    var hasPeopleFilterChecked = (_confluencefilters$pe = confluencefilters.people) !== null && _confluencefilters$pe !== void 0 && _confluencefilters$pe.length ? (_confluencefilters$pe2 = confluencefilters.people) === null || _confluencefilters$pe2 === void 0 ? void 0 : _confluencefilters$pe2.some(function (filter) {
      return filter.isChecked;
    }) : false;
    var hasSiteFilterChecked = (_confluencefilters$si = confluencefilters.site) !== null && _confluencefilters$si !== void 0 && _confluencefilters$si.length ? (_confluencefilters$si2 = confluencefilters.site) === null || _confluencefilters$si2 === void 0 ? void 0 : _confluencefilters$si2.some(function (filter) {
      return filter.isChecked;
    }) : false;
    return hasSpaceFilterChecked || hasPeopleFilterChecked || hasSiteFilterChecked;
  }
  return false;
};
export var UserInputContext = /*#__PURE__*/React.createContext({
  stickySearchEnabled: false,
  query: '',
  showSearchAIDialog: false,
  storeQuery: noop,
  toggleShowSearchAIDialog: noop,
  readFilters: noop,
  storeFilters: noop,
  resetSearchSession: noop,
  resetConfluenceFiltersRef: undefined,
  resetExtensibleFiltersRef: undefined
});
export var UserInputContextProvider = function UserInputContextProvider(_ref) {
  var _ref$stickySearchEnab = _ref.stickySearchEnabled,
    stickySearchEnabled = _ref$stickySearchEnab === void 0 ? false : _ref$stickySearchEnab,
    _ref$sessionResetIdle = _ref.sessionResetIdleTime,
    sessionResetIdleTime = _ref$sessionResetIdle === void 0 ? SESSION_RESET_TIMEOUT_MS : _ref$sessionResetIdle,
    _ref$isExpanded = _ref.isExpanded,
    isExpanded = _ref$isExpanded === void 0 ? false : _ref$isExpanded,
    resetStickySearchRef = _ref.resetStickySearchRef,
    children = _ref.children;
  var _useAnalytics = useAnalytics(),
    fireAnalyticsEvent = _useAnalytics.fireAnalyticsEvent;
  var _useState = useState(getSessionItem(SEARCH_QUERY_KEY) || ''),
    _useState2 = _slicedToArray(_useState, 2),
    query = _useState2[0],
    setQuery = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isStickyUpdated = _useState4[0],
    setIsStickyUpdated = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    stickyQueryUpdated = _useState6[0],
    setStickyQueryUpdated = _useState6[1];
  var queryFirstUpdatedRef = useRef(undefined);
  var lastExpandRef = useRef(false);
  var lastQueryRef = useRef('');
  var timeoutRef = useRef();
  var resetConfluenceFiltersRef = useRef();
  var resetExtensibleFiltersRef = useRef();
  var sessionShowSearchAIDialogStr = getSessionItem(SEARCH_SHOW_AI_DIALOG_KEY);
  var sessionShowSearchAIDialog = sessionShowSearchAIDialogStr ? JSON.parse(sessionShowSearchAIDialogStr) : false;
  var _useState7 = useState(sessionShowSearchAIDialog),
    _useState8 = _slicedToArray(_useState7, 2),
    showSearchAIDialog = _useState8[0],
    setShowSearchAIDialog = _useState8[1];
  var resetSearchSession = useCallback(function () {
    var _resetConfluenceFilte, _resetExtensibleFilte;
    deleteSessionItem(SEARCH_QUERY_KEY);
    deleteSessionItem(SEARCH_FILTERS_KEY);
    deleteSessionItem(SEARCH_SHOW_AI_DIALOG_KEY);
    (_resetConfluenceFilte = resetConfluenceFiltersRef.current) === null || _resetConfluenceFilte === void 0 || _resetConfluenceFilte.call(resetConfluenceFiltersRef);
    (_resetExtensibleFilte = resetExtensibleFiltersRef.current) === null || _resetExtensibleFilte === void 0 || _resetExtensibleFilte.call(resetExtensibleFiltersRef);
    setQuery('');
    setShowSearchAIDialog(false);
    queryFirstUpdatedRef.current = undefined;
    lastQueryRef.current = '';
  }, [resetConfluenceFiltersRef, resetExtensibleFiltersRef]);
  var readFilters = useCallback(function (productId) {
    var searchFilterString = getSessionItem(SEARCH_FILTERS_KEY);
    var filters = searchFilterString ? JSON.parse(searchFilterString) : {};
    if (productId in filters) {
      return filters[productId];
    }
    return {};
  }, []);
  var updateSessionResetTimeout = useCallback(function () {
    if (timeoutRef !== null && timeoutRef !== void 0 && timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(function () {
      if (timeoutRef !== null && timeoutRef !== void 0 && timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      var searchFilterString = getSessionItem(SEARCH_FILTERS_KEY);
      var filters = searchFilterString ? JSON.parse(searchFilterString) : {};
      if (query === '' && !anyConfluenceFiltersChecked(filters)) {
        return; // Skip the reset and analytics event if no filters or query entered
      }
      resetSearchSession();
      fireAnalyticsEvent(onStickySearchCleared({
        timeout: sessionResetIdleTime
      }));
    }, sessionResetIdleTime);
  }, [query, sessionResetIdleTime, resetSearchSession, fireAnalyticsEvent]);
  var storeQuery = useCallback(function (query) {
    setQuery(query);
    setSessionItem(SEARCH_QUERY_KEY, query);
    updateSessionResetTimeout();
    if (queryFirstUpdatedRef.current === undefined) {
      queryFirstUpdatedRef.current = Date.now();
    }
    if (lastQueryRef.current) {
      setStickyQueryUpdated(query !== lastQueryRef.current);
    }
  }, [updateSessionResetTimeout]);
  var toggleShowSearchAIDialog = useCallback(function (showSearchAIDialog) {
    setShowSearchAIDialog(showSearchAIDialog);
    setSessionItem(SEARCH_SHOW_AI_DIALOG_KEY, showSearchAIDialog.toString());
  }, []);
  var storeFilters = useCallback(function (productId, value) {
    var searchFilterString = getSessionItem(SEARCH_FILTERS_KEY);
    var filters = searchFilterString ? JSON.parse(searchFilterString) : {};
    filters[productId] = value;
    setSessionItem(SEARCH_FILTERS_KEY, JSON.stringify(filters));
    if (lastExpandRef.current) {
      updateSessionResetTimeout;
    }
  }, [lastExpandRef, updateSessionResetTimeout]);
  useEffect(function () {
    if (resetStickySearchRef) {
      resetStickySearchRef.current = resetSearchSession;
    }
  });
  useEffect(function () {
    if (!lastExpandRef.current && isExpanded) {
      updateSessionResetTimeout();
    }
    if (lastExpandRef.current && !isExpanded) {
      updateSessionResetTimeout();
    }
  }, [lastExpandRef, isExpanded, updateSessionResetTimeout]);
  useEffect(function () {
    if (!lastExpandRef.current && isExpanded) {
      setIsStickyUpdated(query !== '');
      lastQueryRef.current = query;
      setStickyQueryUpdated(false);
    }
    lastExpandRef.current = isExpanded;
  }, [isExpanded, lastExpandRef, lastQueryRef, query]);
  return /*#__PURE__*/React.createElement(UserInputContext.Provider, {
    value: {
      stickySearchEnabled: stickySearchEnabled,
      query: query,
      showSearchAIDialog: showSearchAIDialog,
      storeQuery: stickySearchEnabled ? storeQuery : noop,
      toggleShowSearchAIDialog: toggleShowSearchAIDialog,
      readFilters: stickySearchEnabled ? readFilters : noop,
      storeFilters: stickySearchEnabled ? storeFilters : noop,
      resetSearchSession: stickySearchEnabled ? resetSearchSession : noop,
      resetConfluenceFiltersRef: stickySearchEnabled ? resetConfluenceFiltersRef : undefined,
      resetExtensibleFiltersRef: stickySearchEnabled ? resetExtensibleFiltersRef : undefined
    }
  }, /*#__PURE__*/React.createElement(SearchDialogAnalyticsContext, {
    analyticContext: stickySearchEnabled ? {
      isSticky: true,
      isStickyUpdated: isStickyUpdated,
      stickyQueryUpdated: stickyQueryUpdated,
      queryUpdatedAgo: queryFirstUpdatedRef.current ? Date.now() - queryFirstUpdatedRef.current : undefined
    } : {
      isSticky: false
    }
  }, children));
};
export var useSessionUserInput = function useSessionUserInput() {
  return useContext(UserInputContext);
};